import axios from 'axios';

const API_BASE = '/api/v1';

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Auth services
export const authService = {
  login: async (emailAddress, password) => {
    const response = await api.put('/entrance/login', { emailAddress, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.get('/account/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/account/me');
      return response.data;
    } catch (error) {
      return null;
    }
  },
};

// Blog services (admin)
export const blogService = {
  getAll: async () => {
    const response = await api.get('/admin/blog/posts');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/blog/posts/${id}`);
    return response.data;
  },

  create: async (postData) => {
    const response = await api.post('/admin/blog/posts', postData);
    return response.data;
  },

  update: async (id, postData) => {
    const response = await api.put(`/admin/blog/posts/${id}`, postData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/blog/posts/${id}`);
    return response.data;
  },
};

// Inquiry services
export const inquiryService = {
  getAll: async () => {
    const response = await api.get('/admin/inquiries');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/inquiries/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/inquiries/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/inquiries/${id}`);
    return response.data;
  },

  convertToClient: async (id) => {
    const response = await api.post(`/admin/inquiries/${id}/convert`);
    return response.data;
  },

  sendEmail: async (id, subject, message) => {
    const response = await api.post(`/admin/inquiries/${id}/send-email`, { subject, message });
    return response.data;
  },

  // Attachment methods
  getAttachments: async (id) => {
    const response = await api.get(`/admin/inquiries/${id}/attachments`);
    return response.data;
  },

  uploadAttachment: async (id, file, { onUploadProgress, signal } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/inquiries/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      onUploadProgress,
      signal,
    });
    return response.data;
  },

  downloadAttachmentUrl: (inquiryId, attachmentId) => {
    return `${api.defaults.baseURL}/admin/inquiries/${inquiryId}/attachments/${attachmentId}/download`;
  },

  deleteAttachment: async (inquiryId, attachmentId) => {
    const response = await api.delete(`/admin/inquiries/${inquiryId}/attachments/${attachmentId}`);
    return response.data;
  },
};

// Client services
export const clientService = {
  getAll: async () => {
    const response = await api.get('/admin/clients');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/clients/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/clients', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/clients/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/clients/${id}`);
    return response.data;
  },
};

// Proposal services
export const proposalService = {
  getAll: async () => {
    const response = await api.get('/admin/proposals');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/proposals/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/proposals', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/proposals/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/proposals/${id}`);
    return response.data;
  },

  previewPdfUrl: (id) => {
    // Return the URL for opening in a new tab
    return `${api.defaults.baseURL}/admin/proposals/${id}/preview-pdf`;
  },

  downloadPdfUrl: (id) => {
    return `${api.defaults.baseURL}/admin/proposals/${id}/download-pdf`;
  },

  send: async (id, emailSubject, emailMessage) => {
    const response = await api.post(`/admin/proposals/${id}/send`, {
      emailSubject,
      emailMessage,
    });
    return response.data;
  },

  // Attachment methods
  getAttachments: async (id) => {
    const response = await api.get(`/admin/proposals/${id}/attachments`);
    return response.data;
  },

  uploadAttachment: async (id, file, { onUploadProgress, signal } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/proposals/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      onUploadProgress,
      signal,
    });
    return response.data;
  },

  downloadAttachmentUrl: (proposalId, attachmentId) => {
    return `${api.defaults.baseURL}/admin/proposals/${proposalId}/attachments/${attachmentId}/download`;
  },

  deleteAttachment: async (proposalId, attachmentId) => {
    const response = await api.delete(`/admin/proposals/${proposalId}/attachments/${attachmentId}`);
    return response.data;
  },
};

// Engagement services
export const engagementService = {
  getAll: async () => {
    const response = await api.get('/admin/engagements');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/engagements/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/engagements', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/engagements/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/engagements/${id}`);
    return response.data;
  },

  // Attachment methods
  getAttachments: async (id) => {
    const response = await api.get(`/admin/engagements/${id}/attachments`);
    return response.data;
  },

  uploadAttachment: async (id, file, { onUploadProgress, signal } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/engagements/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      onUploadProgress,
      signal,
    });
    return response.data;
  },

  downloadAttachmentUrl: (engagementId, attachmentId) => {
    return `${api.defaults.baseURL}/admin/engagements/${engagementId}/attachments/${attachmentId}/download`;
  },

  deleteAttachment: async (engagementId, attachmentId) => {
    const response = await api.delete(`/admin/engagements/${engagementId}/attachments/${attachmentId}`);
    return response.data;
  },
};

// Time Entry services
export const timeEntryService = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/time-entries', { params });
    return response.data;
  },

  getByEngagement: async (engagementId) => {
    const response = await api.get('/admin/time-entries', {
      params: { engagementId },
    });
    return response.data;
  },

  getUnbilled: async (engagementId) => {
    const response = await api.get('/admin/time-entries', {
      params: { engagementId, unbilledOnly: true },
    });
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/time-entries', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/time-entries/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/time-entries/${id}`);
    return response.data;
  },
};

// Invoice services
export const invoiceService = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/invoices', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/invoices/${id}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/invoices', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/invoices/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/invoices/${id}`);
    return response.data;
  },

  previewPdfUrl: (id) => {
    return `${api.defaults.baseURL}/admin/invoices/${id}/preview-pdf`;
  },

  downloadPdfUrl: (id) => {
    return `${api.defaults.baseURL}/admin/invoices/${id}/download-pdf`;
  },

  send: async (id, emailSubject, emailMessage) => {
    const response = await api.post(`/admin/invoices/${id}/send`, {
      emailSubject,
      emailMessage,
    });
    return response.data;
  },
};

// Dashboard stats
export const dashboardService = {
  getStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },
};

export default api;
